import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { loadSensorHistory, loadStatusHistory } from 'redux/graphTemplates/actions';
import {
  getDateRange,
  getFormattedDate,
  PERIOD_TYPE_CUSTOM_RANG,
  PERIOD_TYPE_LAST_8_HOURS,
  periodTypeList
} from 'attrs/chartsTemplate';
import { getGraphsTemplatesReducerState } from 'redux/rootSelectors';
import { debounce, getMachineStatusProps, isSuccess } from 'helpers/utils';
import ReactEcharts from 'echarts-for-react';
import { getMillSensor } from 'attrs/sensorType';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { DateTimeDialog } from 'web-components-ts';
import CircularProgress from '@mui/material/CircularProgress';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import { MACHINE_STATUS_SIMPLE_MAP } from 'attrs/notifications';
import {
  autoRefreshPeriods,
  calculateValue,
  determineDirection,
  findClosestStatusDate,
  sideSpace,
  statusMap
} from 'helpers/chartUtils';
import PropTypes from 'prop-types';
import MobileSensorPortrait from 'components/elements/MobileSensorPortrait';
import { getValueToNullableCell } from 'helpers/stringHandler';
import { COLOR_PRIMARY } from 'attrs/colors';

// Total height of the chart grid in pixels
const chartPixelHeight = 410;
// Total height of the chart container in pixels
const chartPixelHeightContainer = 580;

export default function SensorChart({ machineId, sensorParam }) {
  const theme = useTheme();
  const isDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'));

  const [interval, setInterval] = useState(PERIOD_TYPE_LAST_8_HOURS);
  const [customPeriodStart, setCustomPeriodStart] = useState();
  const [customPeriodEnd, setCustomPeriodEnd] = useState();
  const chartRef = useRef(null);

  const xAxisDefaultValue = [
    {
      type: 'time',
      boundaryGap: false,
      axisLabel: {
        formatter: '{dd}/{MM}/{yyyy}\n{hh}:{mm}:{ss}'
      },
      axisPointer: {
        show: true,
        type: 'line',
        lineStyle: {
          color: 'gray',
          width: 1,
          type: 'dashed',
          shadowBlur: 3,
          shadowColor: 'rgba(0, 0, 0, 1)'
        }
      }
    }
  ];

  const yAxisDefaultValue = [
    {
      data: []
    }
  ];

  const seriesDefaultValue = [
    {
      data: [],
      type: 'line'
    }
  ];

  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const [loadSensorParamsList, setLoadSensorParamsList] = useState([]);
  const [loadStatusParam, setLoadStatusParam] = useState();

  const loadSensorHistoryStatusEqual = (oldObj, newObj) =>
    oldObj?.loadSensorHistoryStatus?.status === newObj?.loadSensorHistoryStatus?.status;
  const { loadSensorHistoryStatus } = useSelector(getGraphsTemplatesReducerState, loadSensorHistoryStatusEqual);

  const sensorHistoryResponseEqual = (oldObj, newObj) => {
    const sameSize = oldObj?.sensorHistoryResponse?.size === newObj?.sensorHistoryResponse?.size;
    const biggerThenZero = oldObj?.sensorHistoryResponse?.size > 0;

    const oldMap = new Map(oldObj?.sensorHistoryResponse);
    const newMap = new Map(newObj?.sensorHistoryResponse);

    const equalsObj = Array.from(oldMap.keys()).every(
      key => JSON.stringify(oldMap.get(key) ?? '') === JSON.stringify(newMap.get(key) ?? '')
    );
    return sameSize && equalsObj && !biggerThenZero;
  };
  const { sensorHistoryResponse } = useSelector(getGraphsTemplatesReducerState, sensorHistoryResponseEqual);

  const { loadStatusHistoryStatus, statusHistoryResponse } = useSelector(getGraphsTemplatesReducerState);
  const [sensorHistoryMap, setSensorHistoryMap] = useState(new Map());
  const [statusHistoryList, setStatusHistoryList] = useState([]);
  const [yAxis, setYAxis] = useState([]);
  const [series, setSeries] = useState([]);
  const didMount = useRef(false);
  const [openCustomDateDialog, setOpenCustomDateDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingTimeOut, setLoadingTimeOut] = useState(0);
  const [openTooltip, setOpenTooltip] = useState(false);
  const [checkedShowMachineStatus, setCheckedShowMachineStatus] = useState(true);
  const [dataZoomRange, setDataZoomRange] = useState({ start: 0, end: 100 });
  const [forceReload, setForceReload] = useState(false);

  // Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState('off');
  const autoRefreshTimerRef = useRef(null);

  const language = useMemo(() => i18n.language, [i18n.language]);

  /**
   * A specific function to find sensor attributes; can be used only in this component
   * @param {*} resultsParam
   * @returns
   */
  function findSensorAttributes(resultsParam) {
    return resultsParam.map(result => {
      const millSensor = getMillSensor(sensorParam.type);
      const name = t(millSensor?.name || sensorParam?.name);
      const unit = millSensor?.unit || sensorParam?.custom_unit || sensorParam?.unit || 'n/a';
      return {
        id: result.sensorId,
        name,
        unit,
        color: COLOR_PRIMARY,
        value: result.closestEntry?.v
      };
    });
  }

  // Build initial structures and params whenever we force reload (interval/custom date or manual refresh)
  useEffect(() => {
    setSeries([]);
    // needed to work with the status line
    setYAxis([
      {
        isStatusYAxis: true,
        type: 'value',
        min: 0,
        max: 100,
        show: false // hide this axis visually
      }
    ]);
    setLoading(true);

    const [pastDate, currentDate] =
      interval !== PERIOD_TYPE_CUSTOM_RANG ? getDateRange(interval) : [customPeriodStart, customPeriodEnd];

    const tempSensorParamsList = [];
    const tempStatusParam = {
      machineId,
      from: pastDate,
      to: currentDate
    };

    const millSensor = getMillSensor(sensorParam?.type);
    const name = millSensor?.name || sensorParam?.name;
    const unit = millSensor?.unit || sensorParam?.custom_unit || sensorParam?.unit || 'n/a';
    const transLatedName = t(`${name}`);

    // IMPORTANT: Here the `sensorId` acts as the key for the history map; we keep it consistent with series.name
    tempSensorParamsList.push({
      sensorId: transLatedName,
      machineId,
      from: pastDate,
      to: currentDate,
      unit,
      sensor_type: sensorParam?.type
    });

    const yAxisSensor = {
      sensorId: transLatedName,
      min: 'dataMin',
      max: 'dataMax',
      type: 'value',
      name: unit,
      show: false,
      axisLine: {
        show: true,
        lineStyle: {
          color: COLOR_PRIMARY
        }
      },
      nameRotate: 45,
      nameTextStyle: {
        fontSize: 14,
        fontWeight: 'bold'
      },
      axisLabel: {
        show: true
      },
      splitLine: {
        show: false
      }
    };

    const seriesSensor = {
      sensorId: transLatedName,
      name: transLatedName,
      type: 'line',
      symbol: 'none',
      color: COLOR_PRIMARY,
      lineStyle: {
        width: 1,
        simplify: true,
        opacity: 1
      },
      yAxisIndex: 1,
      data: [],
      show: true,
      itemStyle: { opacity: 1 },
      progressive: 5000,
      progressiveThreshold: 10000,
      large: true,
      largeThreshold: 2000,
      z: 2,
      zlevel: 0
    };

    setYAxis(prevYAxis => (!prevYAxis?.length ? [yAxisSensor] : [...prevYAxis, yAxisSensor]));
    setSeries(prevSeries => (!prevSeries?.length ? [seriesSensor] : [...prevSeries, seriesSensor]));
    setLoadSensorParamsList(tempSensorParamsList);
    setLoadStatusParam(tempStatusParam);
  }, [setLoadSensorParamsList, setLoadStatusParam, forceReload]);

  // When changes period type (or custom range)
  useEffect(() => {
    if (interval && (interval !== PERIOD_TYPE_CUSTOM_RANG || (customPeriodStart && customPeriodEnd))) {
      if (didMount.current) {
        setLoading(true);
        setDataZoomRange({ start: 0, end: 100 });
        setForceReload(!forceReload);
      } else {
        // This code will run only on the initial mount
        didMount.current = true;
      }
    }
  }, [interval, customPeriodStart, customPeriodEnd]);

  // Fetch sensor history
  useEffect(() => {
    if (loadSensorParamsList?.length) {
      dispatch(loadSensorHistory(loadSensorParamsList));
    }
  }, [dispatch, loadSensorParamsList]);

  // Apply sensor history to series
  useEffect(() => {
    if (isSuccess(loadSensorHistoryStatus.status)) {
      setSensorHistoryMap(sensorHistoryResponse);
    }
  }, [loadSensorHistoryStatus, sensorHistoryResponse]);

  useEffect(() => {
    if (sensorHistoryMap.size) {
      sensorHistoryMap.forEach((value, key) => {
        const type = sensorParam?.type;
        const customUnit = sensorParam?.custom_unit;
        const unit = sensorParam?.unit;
        const millSensor = getMillSensor(type);
        const unitDef = millSensor?.unit || customUnit || unit || 'n/a';

        const newValue =
          value?.map(m => ({
            name: new Date(m.t).toISOString(),
            value: [new Date(m.t).toISOString(), m.v],
            unit: unitDef,
            machineId,
            sensorId: key,
            time: m.t
          })) || [];

        setSeries(prevSeries => prevSeries.map(s => (s?.name === key ? { ...s, data: newValue } : s)));
      });

      // Update Y axis slots visibility/offsets
      let yAxisIndex = 0;
      setYAxis(prevYAxis =>
        prevYAxis.map(y => {
          const willShow = sensorHistoryMap.has(y.sensorId);
          const ny = {
            ...y,
            show: willShow,
            position: determineDirection(yAxisIndex),
            offset: calculateValue(yAxisIndex)
          };
          if (willShow) yAxisIndex += 1;
          return ny;
        })
      );
    }
  }, [sensorHistoryMap, setSeries, machineId, sensorParam]);

  // Fetch machine status
  useEffect(() => {
    if (loadStatusParam) {
      dispatch(loadStatusHistory(loadStatusParam));
    }
  }, [dispatch, loadStatusParam]);

  // Update status list on success
  useEffect(() => {
    if (isSuccess(loadStatusHistoryStatus.status)) {
      setStatusHistoryList(statusHistoryResponse || []);
    }
  }, [loadStatusHistoryStatus, statusHistoryResponse]);

  // Build/replace STATUS-* series whenever statusHistoryList changes
  useEffect(() => {
    if (!sensorHistoryMap?.size || !statusHistoryList?.length) return;

    // Build periods from the statusHistoryList
    const statusPeriods = [];
    statusHistoryList.forEach((status, index) => {
      const nextItem = index < statusHistoryList.length - 1 ? statusHistoryList[index + 1] : status;
      const strCurrentDate = new Date(status.t).toISOString();
      const strNextDate = new Date(nextItem.t).toISOString();
      statusPeriods.push({
        start: strCurrentDate,
        end: strNextDate,
        status: status.v,
        color: statusMap[status.v],
        warning: status.w
      });
    });

    // Build series objects from periods
    const built = Object.values(
      statusPeriods.reduce((acc, period) => {
        const statusKey = `STATUS-${period.status}`;

        if (!acc[statusKey]) {
          acc[statusKey] = {
            name: statusKey,
            type: 'line',
            smooth: false,
            show: checkedShowMachineStatus,
            symbol: 'none',
            yAxisIndex: 0,
            z: 1,
            zlevel: 0,
            lineStyle: {
              width: 12,
              color: period.color,
              simplify: true,
              opacity: checkedShowMachineStatus ? 0.6 : 0
            },
            data: []
          };
        }

        // Start the period with the beginning point
        acc[statusKey].data.push({
          name: new Date(period.start).toISOString(),
          value: [new Date(period.start).toISOString(), 1.6],
          time: period.start
        });

        // End the period with the ending point and add a null to create a gap
        acc[statusKey].data.push(
          {
            name: new Date(period.end).toISOString(),
            value: [new Date(period.end).toISOString(), 1.6],
            time: period.end
          },
          null // gap between this and the next period
        );

        if (period.warning) {
          const warningKey = 'STATUS-WARNING';
          if (!acc[warningKey]) {
            acc[warningKey] = {
              name: warningKey,
              type: 'line',
              smooth: false,
              show: true,
              symbol: 'none',
              yAxisIndex: 0,
              z: 1,
              zlevel: 0,
              lineStyle: {
                width: 6,
                color: '#F8CC84',
                simplify: true,
                opacity: 0.6
              },
              data: []
            };
          }

          acc[warningKey].data.push({
            name: new Date(period.start).toISOString(),
            value: [new Date(period.start).toISOString(), 3.9],
            time: period.start
          });

          acc[warningKey].data.push(
            {
              name: new Date(period.end).toISOString(),
              value: [new Date(period.end).toISOString(), 3.9],
              time: period.end
            },
            null
          );
        }

        return acc;
      }, {})
    );

    // Replace any existing STATUS-* series with the fresh ones
    setSeries(prev => {
      const nonStatus = prev.filter(s => !s.name.includes('STATUS-'));
      return [...nonStatus, ...built];
    });

    // Loading delay (kept simple here)
    const totalItems =
      sensorHistoryMap instanceof Map
        ? Array.from(sensorHistoryMap.values()).reduce((max, arr) => Math.max(max, arr?.length || 0), 0)
        : 0;

    let delay = totalItems * 0.3;
    delay = delay < 3000 ? 3000 : delay;

    clearTimeout(loadingTimeOut);
    setLoadingTimeOut(
      setTimeout(() => {
        setLoading(false);
      }, delay)
    );
  }, [sensorHistoryMap, statusHistoryList, checkedShowMachineStatus]);

  // Enforce zoom range 0..100
  const validateZoomRange = (start, end) => ({
    start: Math.max(0, Math.min(start, 100)),
    end: Math.max(0, Math.min(end, 100))
  });

  // ECharts handlers (zoom + window resize)
  useEffect(() => {
    const chartInstance = chartRef?.current?.getEchartsInstance();
    if (!chartInstance) return;

    const onZoomRaw = params => {
      const zoom = params.batch ? params.batch[0] : params;
      setDataZoomRange(validateZoomRange(zoom.start, zoom.end));
    };

    const onResizeRaw = () => {
      if (chartRef.current) chartRef.current.getEchartsInstance().resize();
    };

    const onZoom = debounce(onZoomRaw, 400);
    const onResize = debounce(onResizeRaw, 200);

    chartInstance.on('dataZoom', onZoom);
    window.addEventListener('resize', onResize);

    // proper cleanup need to be an arrow function, but ESLint warns about consistent-return
    // eslint-disable-next-line consistent-return
    return () => {
      chartInstance.off('dataZoom', onZoom);
      window.removeEventListener('resize', onResize);
      onZoom.cancel?.();
      onResize.cancel?.();
    };
  }, [chartRef, validateZoomRange]);

  function getLeftPadding() {
    return sideSpace(sensorHistoryMap?.size)[0];
  }

  function getRightPadding() {
    return sideSpace(sensorHistoryMap?.size)[1];
  }

  const options = useMemo(
    () => ({
      textStyle: {
        fontFamily: 'Roboto, sans-serif'
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(43, 47, 66, 0.96)',
        textStyle: {
          color: '#ffffff',
          fontFamily: 'Roboto, sans-serif'
        },
        confine: true,
        position(point, _params, _dom, _rect, size) {
          const [x, y] = point;
          const { contentSize } = size;
          const posX = x + contentSize[0] > size.viewSize[0] - 42 ? x - contentSize[0] - 42 : x + 42;
          const posY = y + contentSize[1] > size.viewSize[1] ? y - contentSize[1] : y;
          return [posX, posY];
        },
        formatter(params) {
          let result = '';
          const currentTooltipTime = params[0].axisValue;
          if (statusHistoryList.length) {
            const status = findClosestStatusDate(currentTooltipTime, statusHistoryList);
            if (status?.v) {
              const hasWarning = status.w;
              const statusDetail = getMachineStatusProps(status.v);
              const statusWarning = t('enum.machine_status.WARNING').toUpperCase();
              const statusDesc = t(`enum.machine_status.${statusDetail.value}`).toUpperCase();
              const ColorSquare = `
                <span
                  style="
                    display:inline-block;
                    margin-right:5px;
                    width:10px;
                    height:10px;
                    background-color:${statusDetail.color};
                  "
                ></span>`;
              const Chip = hasWarning
                ? `
                  <div
                    style="
                      background-color: #ffcc00;
                      color: white;
                      padding: 3px 6px;
                      border-radius: 16px;
                      display: inline-block;
                      font-size: 12px;
                      line-height: 12px;
                    "
                  >
                    ${statusWarning}
                  </div>
                `
                : '';
              result += `${ColorSquare}${statusDesc}${Chip}<br/><br/>`;
            }
          }

          const sensorIdsInParams = new Set(params.map(p => p.data.sensorId));
          const filteredSensorHistory = [...sensorHistoryMap].filter(([sensorId]) => !sensorIdsInParams.has(sensorId));

          const nearestSensors = filteredSensorHistory.map(([sensorId, historyList]) => {
            if (!historyList || historyList.length === 0) {
              return { sensorId, closestEntry: null };
            }
            const closestEntry = historyList.reduce((prev, curr) =>
              Math.abs(curr.t - currentTooltipTime) < Math.abs(prev.t - currentTooltipTime) ? curr : prev
            );
            return { sensorId, closestEntry };
          });

          const paramAttributes = params.reduce((acc, param) => {
            if (!param.seriesName.includes('STATUS-')) {
              acc.push({
                id: param.data.sensorId,
                name: param.seriesName,
                unit: param.data.unit,
                color: param.color,
                value: param.value[1]
              });
            }
            return acc;
          }, []);

          const nearestSensorsAttribute = findSensorAttributes(nearestSensors);

          const combinedList = paramAttributes
            .concat(nearestSensorsAttribute)
            .filter((o, i, arr) => arr.findIndex(x => x.name === o.name) === i);

          combinedList.sort((a, b) => a.name.localeCompare(b.name));

          const solidLine = '<div style="border-top: 2px solid #FFFFFF;margin: 6px 0;"></div>';
          result += combinedList
            .map(
              (sensor, i) => `
                ${i > 0 ? solidLine : ''}
                <span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;
                height:10px;background-color:${sensor.color};"></span>${sensor.name}<br/>
                <span style="font-size:16px;font-weight:bold;margin: 6px 0;">${getValueToNullableCell(sensor.value)} ${
                sensor.unit
              }</span>`
            )
            .join('<br/>');
          if (combinedList.length > 0) result += solidLine;
          result += `<p style="margin: 3px 0;"> ${getFormattedDate(currentTooltipTime)}</p>`;
          return result;
        }
      },
      grid: {
        top: '80px',
        left: getLeftPadding(),
        right: getRightPadding(),
        height: `${chartPixelHeight}px`
      },
      xAxis: xAxisDefaultValue,
      yAxis: yAxis.length > 0 ? yAxis : yAxisDefaultValue,
      dataZoom: [
        {
          type: 'slider',
          filterMode: 'none',
          start: dataZoomRange.start,
          end: dataZoomRange.end,
          labelFormatter(value) {
            return `${getFormattedDate(value)}   `;
          }
        }
      ],
      series: series.length > 0 ? series : seriesDefaultValue
    }),
    [yAxis, series, statusHistoryList, sensorHistoryMap, dataZoomRange, t]
  );

  /**
   * Clears the existing auto-refresh timer if it is currently active.
   * If the timer is cleared, the reference to it is set to null.
   */
  function clearAutoRefreshTimer() {
    if (autoRefreshTimerRef.current) {
      clearInterval(autoRefreshTimerRef.current);
      autoRefreshTimerRef.current = null;
    }
  }

  const onChangeInterval = event => {
    const intervalParam = event.target.value;
    if (intervalParam !== PERIOD_TYPE_CUSTOM_RANG) setInterval(intervalParam);

    // Disable auto-refresh when not in LAST_8_HOURS
    if (intervalParam !== PERIOD_TYPE_LAST_8_HOURS) {
      setAutoRefresh('off');
      clearAutoRefreshTimer();
    }
  };

  const handleOpenCustomDateDialog = () => {
    setOpenCustomDateDialog(true);
  };

  const handleCloseCustomDateDialog = () => {
    setOpenCustomDateDialog(false);
  };

  const handleSetValuesCustomPeriod = (customPeriodStartParam, customPeriodEndParam) => {
    setInterval(PERIOD_TYPE_CUSTOM_RANG);
    setCustomPeriodStart(customPeriodStartParam);
    setCustomPeriodEnd(customPeriodEndParam);
    setAutoRefresh('off');
    clearAutoRefreshTimer();
  };

  const handleTooltipClose = () => {
    setOpenTooltip(false);
  };

  const handleTooltipOpen = () => {
    setOpenTooltip(true);
  };

  const toggleStatusVisibility = prevSeries =>
    prevSeries.map(s => {
      if (s.name.includes('STATUS-')) {
        const show = !s.show;
        return {
          ...s,
          show,
          lineStyle: { ...s.lineStyle, opacity: show ? 0.6 : 0 }
        };
      }
      return s;
    });

  const handleCheckboxChange = event => {
    setCheckedShowMachineStatus(event.target.checked);
    setSeries(prevSeries => toggleStatusVisibility(prevSeries));
  };

  const handleRefreshInfo = () => {
    // Manual refresh: rebuild params for the current interval window
    setForceReload(prev => !prev);
  };

  /**
   * Auto refresh interval
   * Creates a timer based on autoRefresh value and refetches history/status on each tick.
   * Enabled only when interval === LAST_8_HOURS to keep a moving window.
   */
  useEffect(() => {
    // Always clear any previous timer
    clearAutoRefreshTimer();

    // Only allow auto-refresh on LAST_8_HOURS
    if (interval !== PERIOD_TYPE_LAST_8_HOURS) return;

    // Resolve interval ms from the key
    const selected = autoRefreshPeriods.find(p => p.key === autoRefresh);
    const intervalMs = selected?.value ?? 0;
    if (!intervalMs) return; // 'off'

    // Build moving window from current time/interval
    const buildParams = () => {
      const [fromDate, toDate] = getDateRange(PERIOD_TYPE_LAST_8_HOURS);
      const sensorParams = (loadSensorParamsList || []).map(item => ({
        ...item,
        from: fromDate,
        to: toDate
      }));
      const statusParam = loadStatusParam
        ? {
            ...loadStatusParam,
            from: fromDate,
            to: toDate
          }
        : null;
      return { sensorParams, statusParam };
    };

    const getData = () => {
      const { sensorParams, statusParam } = buildParams();
      if (sensorParams.length) dispatch(loadSensorHistory(sensorParams));
      if (statusParam) dispatch(loadStatusHistory(statusParam));
    };

    // Fire once (if not initial loading overlay)
    if (!loading) getData();

    // Then schedule
    autoRefreshTimerRef.current = window.setInterval(getData, intervalMs);

    // proper cleanup need to be an arrow function, but ESLint warns about consistent-return
    // eslint-disable-next-line consistent-return
    return () => {
      clearAutoRefreshTimer();
    };
  }, [autoRefresh, interval, loadSensorParamsList, loadStatusParam, dispatch, loading]);

  return (
    <Paper
      sx={{
        overflow: 'hidden',
        width: '100%',
        p: { xs: 1, sm: 2, md: 2, lg: 3 }
      }}
      data-selector="chart-template-details"
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={9.5} lg sx={{ order: { xs: 1, sm: 1, md: 1, lg: 1 }, display: 'flex' }}>
          <FormControl variant="filled" sx={{ minWidth: { xs: '100%', sm: '100%', md: '305px' } }} disabled={loading}>
            <InputLabel id="template-label">{t('chart_template_details.filter_time')}</InputLabel>
            <Select
              IconComponent={KeyboardArrowDownIcon}
              labelId="period-type-label"
              id="period-type-select"
              onChange={onChangeInterval}
              value={interval}
            >
              {periodTypeList.map(periodType => (
                <MenuItem key={periodType} value={periodType}>
                  {t(`enum.chart_templates.period.${periodType}`)}
                </MenuItem>
              ))}
              <MenuItem
                key={`${PERIOD_TYPE_CUSTOM_RANG}`}
                value={`${PERIOD_TYPE_CUSTOM_RANG}`}
                onClick={handleOpenCustomDateDialog}
              >
                {t('enum.chart_templates.period.PERIOD_TYPE_CUSTOM_RANG')}
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {!isDownMd && (
          <Grid item md="auto" lg="auto" sx={{ order: { md: 4, lg: 2 }, display: 'flex', alignItems: 'center' }}>
            <ClickAwayListener onClickAway={handleTooltipClose}>
              <div>
                <Tooltip
                  arrow
                  PopperProps={{ disablePortal: true }}
                  onClose={handleTooltipClose}
                  open={openTooltip}
                  disableFocusListener
                  disableHoverListener
                  disableTouchListener
                  title={
                    <Box>
                      {MACHINE_STATUS_SIMPLE_MAP.map(status => (
                        <Box key={status.value} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Box sx={{ width: 16, height: 16, backgroundColor: status.color, mr: 1 }} />
                          <Typography variant="body2">{t(`enum.machine_status.${status.value}`)}</Typography>
                        </Box>
                      ))}
                    </Box>
                  }
                >
                  <Button sx={{ textTransform: 'none' }} startIcon={<InfoOutlinedIcon />} onClick={handleTooltipOpen}>
                    <Typography sx={{ fontSize: '14px', color: '#000000' }}>
                      {t('chart_template_details.legend')}
                    </Typography>
                  </Button>
                </Tooltip>
              </div>
            </ClickAwayListener>
          </Grid>
        )}

        <Grid item xs={12} sm={12} md lg="auto" sx={{ order: { xs: 4, sm: 4, md: 5, lg: 3 }, display: 'flex' }}>
          <FormControlLabel
            control={<Checkbox checked={checkedShowMachineStatus} onChange={handleCheckboxChange} />}
            label={t('chart_template_details.show_machine_status')}
          />
        </Grid>

        <Grid
          item
          xs="auto"
          sm="auto"
          md={0.5}
          lg="auto"
          sx={{
            order: { xs: 2, sm: 2, md: 2, lg: 4 },
            display: 'flex',
            marginTop: { xs: 2, sm: 2, md: 0, lg: 0 },
            '& button span': { marginRight: '0' }
          }}
        >
          <Button startIcon={<RefreshOutlinedIcon />} onClick={handleRefreshInfo} disabled={loading} />
        </Grid>

        {/* Auto-refresh picker */}
        <Grid
          item
          xs
          sm={4}
          md={2}
          lg="auto"
          sx={{
            order: { xs: 3, sm: 3, md: 3, lg: 5 },
            display: 'flex',
            justifyContent: { sm: 'flex-start', md: 'flex-end' },
            marginTop: { xs: 2, sm: 2, md: 0, lg: 0 }
          }}
        >
          <Select
            id="auto-refresh-picker"
            variant="outlined"
            value={autoRefresh}
            onChange={event => setAutoRefresh(event.target.value)}
            IconComponent={ArrowDropDownIcon}
            data-testid="auto-refresh-info-picker"
            disabled={loading || interval !== PERIOD_TYPE_LAST_8_HOURS}
            sx={{
              minWidth: '50px',
              border: 'none',
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '& .MuiSelect-select': {
                color: 'primary.main',
                backgroundColor: 'transparent',
                transition: 'background-color 0.2s ease'
              },
              '&:hover .MuiSelect-select': {
                backgroundColor: componentTheme =>
                  loading || interval !== PERIOD_TYPE_LAST_8_HOURS
                    ? 'transparent'
                    : alpha(componentTheme.palette.primary.main, 0.04)
              },
              '& .MuiSelect-icon': {
                color: componentTheme =>
                  loading || interval !== PERIOD_TYPE_LAST_8_HOURS
                    ? componentTheme.palette.action.disabled
                    : componentTheme.palette.primary.main
              }
            }}
          >
            {autoRefreshPeriods.map(item => (
              <MenuItem key={item.key} value={item.key}>
                {t(`chart_template_details.refresh.${item.key}`)}
              </MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>

      <Stack spacing={4}>
        {isDownSm ? (
          <Box sx={{ pt: 4 }}>
            <MobileSensorPortrait />
          </Box>
        ) : (
          <div style={{ position: 'relative' }} id="graph-template">
            {loading && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 1000
                }}
              >
                <CircularProgress disableShrink />
              </div>
            )}
            <ReactEcharts
              ref={chartRef}
              data-selector="chart-template-canvas"
              option={options}
              opts={{ renderer: 'canvas' }}
              notMerge
              lazyUpdate
              style={{
                minHeight: `${chartPixelHeightContainer}px`,
                position: 'relative',
                overflow: 'hidden'
              }}
            />
          </div>
        )}
      </Stack>

      <DateTimeDialog
        open={openCustomDateDialog}
        handleClose={handleCloseCustomDateDialog}
        title={t('custom_date_dialog.title')}
        subTitle={t('custom_date_dialog.subtitle')}
        startTimeCaption={t('custom_date_dialog.start_time')}
        endTimeCaption={t('custom_date_dialog.end_time')}
        cancelCaption={t('default_actions.cancel')}
        okCaption={t('default_actions.apply')}
        setValues={handleSetValuesCustomPeriod}
        maxDays={6}
        language={language}
      />
    </Paper>
  );
}

SensorChart.propTypes = {
  machineId: PropTypes.string.isRequired,
  sensorParam: PropTypes.shape().isRequired
};
